'use client'

import { navigatePublicSearch } from '@/actions/public-search'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronDown, LoaderCircle, Search, X } from 'lucide-react'
import { unstable_rethrow, usePathname, useSearchParams } from 'next/navigation'
import { useTopLoader } from 'nextjs-toploader'
import { useRouter } from 'nextjs-toploader/app'
import { useEffect, useId, useRef, useState, useTransition } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import {
  compatibleFilterFields,
  draftSchema,
  draftSignature,
  normaliseDraft,
} from './search-filter-helpers'
import SearchFilterPicker from './search-filter-picker'

export default function PublicSearchForm({
  fields,
  applied = {},
  action,
  encode,
  games = [],
  platformKey = 'platforms',
  keywordPlaceholder = 'Search communities, tracks etc...',
  advancedId,
  onSubmit: customSubmit,
  onApplied,
  onTrack,
  maxQueryLength,
  className = '',
  layout = 'default',
  showClearAll = true,
  showUnappliedChanges = true,
  showUpdatingResults = true,
  navigateImmediately = false,
}) {
  const router = useRouter()
  const progress = useTopLoader()
  const pathname = usePathname()
  const urlParams = useSearchParams()
  const generatedId = useId()
  const panelId = advancedId || `${generatedId}-advanced`
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [pending, startTransition] = useTransition()
  const inFlight = useRef(false)
  const resolvedDefaults = normaliseDraft(fields)
  const appliedValues = normaliseDraft(fields, applied)
  const appliedKey = draftSignature(appliedValues)
  const navigationKey = urlParams.toString()
  const form = useForm({
    defaultValues: appliedValues,
    resolver: zodResolver(draftSchema(fields)),
  })
  const draft = useWatch({ control: form.control })
  const activeFields = compatibleFilterFields(fields, draft, games, platformKey)
  const activeSchema = draftSchema(activeFields)
  const resetRef = useRef({ appliedValues, reset: form.reset })
  resetRef.current = { appliedValues, reset: form.reset }
  useEffect(() => {
    resetRef.current.reset(resetRef.current.appliedValues)
    setReady(true)
    setError('')
  }, [appliedKey, navigationKey])
  const dirty = draftSignature(draft) !== appliedKey
  const update = (key, value) => {
    const field = fields.find((entry) => entry.key === key)
    const chosen = value == null ? resolvedDefaults[key] : value
    form.setValue(key, chosen, { shouldDirty: true })
    form.clearErrors(field.key)
    setError('')
  }
  const submit = async (event) => {
    event.preventDefault()
    if (inFlight.current || pending) return
    const parsed = activeSchema.safeParse(form.getValues())
    if (!parsed.success) {
      setError(parsed.error.issues[0].message)
      return
    }
    inFlight.current = true
    setError('')
    startTransition(async () => {
      const actionNavigation = !navigateImmediately || Boolean(customSubmit)
      if (actionNavigation) progress.start()
      try {
        const params = encode(parsed.data)
        if (onTrack) onTrack(Object.fromEntries(params))
        if (customSubmit) await customSubmit(Object.fromEntries(params))
        else {
          const target = `${action || pathname}${params.size ? `?${params.toString()}` : ''}`
          if (navigateImmediately) router.push(target)
          else await navigatePublicSearch(target)
        }
        onApplied?.(parsed.data)
      } catch (error) {
        // Next.js redirects are control flow, not failed searches.
        unstable_rethrow(error)
        setError(
          'Could not update results. Your choices are saved here. Select Search to try again.',
        )
      } finally {
        if (actionNavigation) progress.done()
        inFlight.current = false
      }
    })
  }
  const renderField = (field) =>
    field.kind === 'text' ? (
      <label
        key={field.key}
        className="flex min-w-0 flex-col gap-2 text-sm font-semibold text-neutral-blue-200"
      >
        {field.label}
        <input
          {...form.register(field.key)}
          disabled={!ready || pending}
          maxLength={maxQueryLength || field.maxLength}
          placeholder={keywordPlaceholder}
          className="min-h-14 w-full rounded-md border border-neutral-blue-500 bg-white px-3 font-semibold text-neutral-blue-900 placeholder:text-slate-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        />
      </label>
    ) : (
      <SearchFilterPicker
        key={field.key}
        label={field.label}
        value={draft[field.key]}
        onChange={(value) => update(field.key, value)}
        options={field.options}
        multi={field.multi}
        searchable={
          field.searchable === 'long'
            ? field.options.length > 8
            : field.searchable
        }
        initializing={!ready}
        disabled={pending || field.disabled}
        loading={field.loading}
      />
    )
  const chips = activeFields.flatMap((field) => {
    const value = draft[field.key]
    if (
      !value ||
      draftSignature({ value }) ===
        draftSignature({ value: resolvedDefaults[field.key] })
    )
      return []
    return (Array.isArray(value) ? value : [value]).map((entry) => ({
      field,
      value: entry,
      label:
        field.options?.find((option) => option.value === entry)?.label || entry,
    }))
  })
  const advancedCount = new Set(
    chips.filter(({ field }) => field.advanced).map(({ field }) => field.key),
  ).size
  const clearFiltersButton = (
    <button
      type="button"
      disabled={!ready || pending}
      onClick={() => {
        form.reset(resolvedDefaults)
        setError('')
      }}
      className="min-h-11 cursor-pointer rounded px-2 text-sm text-neutral-blue-200 underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-orange-500"
    >
      Clear all filters
    </button>
  )
  return (
    <form
      aria-label="Search filters"
      onSubmit={submit}
      aria-busy={!ready || pending}
      className={`w-full ${className}`}
    >
      <div className="min-[1200px]:grid min-[1200px]:grid-cols-[minmax(0,1fr)_10rem] min-[1200px]:gap-x-4">
        <div
          className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${layout === 'homepage' ? 'min-[1200px]:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1.6fr)]!' : 'min-[1200px]:grid-cols-4!'}`}
        >
          {activeFields.filter((field) => !field.advanced).map(renderField)}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 min-[1200px]:contents">
          <button
            type="submit"
            disabled={!ready || pending}
            aria-label={!ready ? 'Loading search filters' : undefined}
            className={`bg-gf-gradient flex min-h-12 w-40 cursor-pointer items-center justify-center gap-2 rounded-md px-4 font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 min-[1200px]:col-start-2 min-[1200px]:row-start-1 min-[1200px]:min-h-14 min-[1200px]:self-end ${ready ? 'disabled:cursor-wait disabled:opacity-60' : 'disabled:cursor-wait disabled:opacity-100'}`}
          >
            {!ready || pending ? (
              <LoaderCircle
                className="size-5 motion-safe:animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Search className="size-5" aria-hidden="true" />
            )}
            {!ready ? 'Loading…' : pending ? 'Searching…' : 'Search'}
          </button>
          {showClearAll && (
            <div className="hidden md:block min-[1200px]:col-start-1 min-[1200px]:row-start-2 min-[1200px]:mt-4">
              {clearFiltersButton}
            </div>
          )}
          {activeFields.some((field) => field.advanced) && (
            <button
              type="button"
              aria-expanded={advancedOpen}
              aria-controls={panelId}
              onClick={() => setAdvancedOpen(!advancedOpen)}
              className="ml-auto flex min-h-11 cursor-pointer items-center gap-2 rounded px-1 text-sm text-neutral-blue-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 min-[1200px]:col-span-2 min-[1200px]:col-start-1 min-[1200px]:row-start-2 min-[1200px]:mt-4 min-[1200px]:justify-self-end"
            >
              <span className="underline underline-offset-4">
                More filters{advancedCount > 0 ? ` (${advancedCount})` : ''}
              </span>
              <ChevronDown
                aria-hidden="true"
                className={`size-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none ${advancedOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
          {showClearAll && (
            <div className="md:hidden">{clearFiltersButton}</div>
          )}
        </div>
      </div>
      {activeFields.some((field) => field.advanced) && (
        <div
          id={panelId}
          hidden={!advancedOpen}
          className="mt-4 border-t border-neutral-blue-700 pt-5"
        >
          {advancedOpen && (
            <div className="min-[1200px]:grid-cols-4! grid grid-cols-1 gap-4 sm:grid-cols-2">
              {activeFields.filter((field) => field.advanced).map(renderField)}
            </div>
          )}
        </div>
      )}
      {chips.length > 0 && (
        <div
          aria-label="Selected filters"
          className={`mt-4 flex flex-wrap gap-2 ${!advancedOpen && advancedCount > 0 ? '' : 'md:hidden'}`}
        >
          {chips.map(({ field, value, label }) => (
            <button
              key={`${field.key}-${value}`}
              type="button"
              disabled={pending}
              aria-label={`Remove ${field.label}: ${label}`}
              onClick={() =>
                update(
                  field.key,
                  field.multi
                    ? draft[field.key].filter((entry) => entry !== value)
                    : resolvedDefaults[field.key],
                )
              }
              className={`flex min-h-11 max-w-full items-center gap-2 rounded-full border border-neutral-blue-500 px-3 py-2 text-left text-sm text-white hover:bg-neutral-blue-800 focus-visible:outline-2 focus-visible:outline-orange-500 md:min-h-8 md:py-1 ${field.advanced && !advancedOpen ? '' : 'md:hidden'}`}
            >
              <span>
                {field.label}: {label}
              </span>
              <X className="size-4 shrink-0" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {showUnappliedChanges && dirty && (
        <p role="status" className="mt-3 text-sm text-neutral-blue-200">
          Changes not applied. Select Search.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-300">
          {error}
        </p>
      )}
      {showUpdatingResults && pending && (
        <p role="status" className="mt-3 text-sm text-neutral-blue-200">
          Updating results…
        </p>
      )}
    </form>
  )
}
